/**
 * Phase 89E.c — Super Admin Webhook Health Dashboard Tile
 *
 * Cross-tenant observability over the per-academy outbound webhook
 * subscriptions delivered by the Phase 89E dispatcher.  Reads only the
 * REAL collections (`db.webhook_endpoints` / `db.webhook_deliveries`).
 *
 * Wires to:
 *   GET    /api/super-admin/webhook-health/endpoints
 *   POST   /api/super-admin/webhook-health/endpoints/{id}/ping-test
 *   POST   /api/super-admin/webhook-health/endpoints/{id}/force-reactivate
 *   POST   /api/super-admin/webhook-health/endpoints/{id}/revoke-secret
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Activity, RefreshCw, AlertTriangle, CheckCircle2, ShieldOff, Power, KeyRound,
  Send, MoreHorizontal, Copy, Loader2, Webhook,
} from 'lucide-react';
import { toast } from 'sonner';

const BUCKET_CONFIG = {
  active: {
    label: 'Active',
    className: 'bg-emerald-600 hover:bg-emerald-600 text-white',
    icon: CheckCircle2,
  },
  degraded: {
    label: 'Degraded',
    className: 'border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40',
    icon: AlertTriangle,
    variant: 'outline',
  },
  dead: {
    label: 'Dead',
    className: 'border-rose-400 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40',
    icon: ShieldOff,
    variant: 'outline',
  },
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const BucketBadge = ({ status }) => {
  const cfg = BUCKET_CONFIG[status] || { label: status, className: '' };
  const Icon = cfg.icon || Activity;
  return (
    <Badge
      variant={cfg.variant || 'default'}
      className={`text-[10px] ${cfg.className}`}
      data-testid={`webhook-health-bucket-${status}`}
    >
      <Icon className="h-3 w-3 mr-1" />
      {cfg.label}
    </Badge>
  );
};

const OverviewMetric = ({ label, value, tone, testid }) => {
  const palette = {
    slate: 'text-slate-700 dark:text-slate-200',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
    rose: 'text-rose-700 dark:text-rose-300',
  }[tone || 'slate'];
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-2xl font-display font-semibold tabular-nums ${palette}`} data-testid={testid}>
        {value}
      </span>
    </div>
  );
};

export default function WebhookHealthTile() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionPending, setActionPending] = useState(null); // {id, action}
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [revealedSecret, setRevealedSecret] = useState(null); // {endpointId, secret}

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const r = await api.get(`/super-admin/webhook-health/endpoints?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load webhook health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(false); }, [load]);

  const triggerAction = useCallback(async (endpoint, action) => {
    setActionPending({ id: endpoint.id, action });
    try {
      if (action === 'ping-test') {
        const r = await api.post(`/super-admin/webhook-health/endpoints/${endpoint.id}/ping-test`);
        const d = r.data || {};
        if (d.status === 'delivered') {
          toast.success(`Ping delivered · HTTP ${d.status_code}`);
        } else if (d.status_code) {
          toast.warning(`Ping returned HTTP ${d.status_code}`);
        } else {
          toast.error(`Ping failed · ${d.error || 'unknown error'}`);
        }
      } else if (action === 'force-reactivate') {
        await api.post(`/super-admin/webhook-health/endpoints/${endpoint.id}/force-reactivate`);
        toast.success(`Reactivated · ${endpoint.academy_name}`);
      } else if (action === 'revoke-secret') {
        const r = await api.post(`/super-admin/webhook-health/endpoints/${endpoint.id}/revoke-secret`);
        setRevealedSecret({ endpointId: endpoint.id, secret: r.data.secret, endpointName: endpoint.name });
        toast.success('Secret rotated — copy it now, it will not be shown again.');
      }
      await load(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed: ${action}`);
    } finally {
      setActionPending(null);
    }
  }, [load]);

  const overview = data?.overview || { total: 0, active: 0, degraded: 0, dead: 0 };
  const deliveries7d = data?.deliveries_7d || {};
  const rows = data?.rows || [];

  const totals = useMemo(() => ({
    delivered: deliveries7d.delivered || 0,
    failed: deliveries7d.failed || 0,
    pending: deliveries7d.pending || 0,
    skipped: deliveries7d.skipped || 0,
  }), [deliveries7d]);

  if (loading) {
    return (
      <Card data-testid="webhook-health-tile-loading">
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent><Skeleton className="h-40 w-full" /></CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="webhook-health-tile">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-950">
              <Webhook className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-base">Webhook Health</CardTitle>
              <p className="text-xs text-muted-foreground">Cross-tenant outbound dispatcher monitor</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="webhook-health-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All endpoints</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="degraded">Degraded</SelectItem>
                <SelectItem value="dead">Dead</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="webhook-health-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overview metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-muted/30">
          <OverviewMetric label="Total endpoints" value={overview.total} tone="slate" testid="webhook-health-metric-total" />
          <OverviewMetric label="Active" value={overview.active} tone="emerald" testid="webhook-health-metric-active" />
          <OverviewMetric label="Degraded" value={overview.degraded} tone="amber" testid="webhook-health-metric-degraded" />
          <OverviewMetric label="Dead" value={overview.dead} tone="rose" testid="webhook-health-metric-dead" />
        </div>

        {/* 7-day delivery throughput strip */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground border border-dashed rounded-lg p-2.5">
          <span className="font-medium text-slate-700 dark:text-slate-200">Last 7 days deliveries:</span>
          <span className="inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
            <span className="tabular-nums" data-testid="webhook-health-7d-delivered">{totals.delivered}</span> delivered
          </span>
          <span className="inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-rose-600" />
            <span className="tabular-nums" data-testid="webhook-health-7d-failed">{totals.failed}</span> failed
          </span>
          <span className="inline-flex items-center gap-1">
            <Activity className="h-3 w-3 text-amber-600" />
            <span className="tabular-nums" data-testid="webhook-health-7d-pending">{totals.pending}</span> pending
          </span>
          <span className="inline-flex items-center gap-1 opacity-70">
            <span className="tabular-nums">{totals.skipped}</span> skipped
          </span>
        </div>

        {/* Health table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="webhook-health-table">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Endpoint</th>
                  <th className="px-3 py-2 font-medium">Tenant</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Sent / Failed</th>
                  <th className="px-3 py-2 font-medium">Last activity</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground" data-testid="webhook-health-empty">
                    No webhook endpoints match this filter.
                  </td></tr>
                )}
                {rows.map((row) => {
                  const lastActivity = row.last_delivered_at || row.last_failed_at;
                  const isActionPending = actionPending && actionPending.id === row.id;
                  return (
                    <tr
                      key={row.id}
                      className="border-t hover:bg-muted/20 align-top"
                      data-testid={`webhook-health-row-${row.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800 dark:text-slate-100">{row.name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono break-all">{row.url}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(row.events || []).slice(0, 3).map((ev) => (
                            <Badge key={ev} variant="secondary" className="text-[9px] font-mono">{ev}</Badge>
                          ))}
                          {(row.events || []).length > 3 && (
                            <Badge variant="secondary" className="text-[9px]">+{row.events.length - 3}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-700 dark:text-slate-200">{row.academy_name || '—'}</div>
                        {row.academy_slug && (
                          <div className="text-[10px] text-muted-foreground font-mono">{row.academy_slug}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <BucketBadge status={row.health_status} />
                        {!row.is_active && (
                          <div className="text-[10px] text-rose-600 mt-1">paused</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="text-emerald-700 dark:text-emerald-400">{row.delivery_count}</div>
                        <div className="text-rose-600 dark:text-rose-400">{row.failure_count} failed</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-700 dark:text-slate-200">{fmtTs(lastActivity)}</div>
                        {row.last_failed_at && row.last_delivered_at && row.last_failed_at > row.last_delivered_at && (
                          <div className="text-[10px] text-rose-600">last attempt failed</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={isActionPending}
                              data-testid={`webhook-health-actions-${row.id}`}
                            >
                              {isActionPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onClick={() => triggerAction(row, 'ping-test')}
                              data-testid={`webhook-health-action-ping-${row.id}`}
                            >
                              <Send className="h-3.5 w-3.5 mr-2" />
                              Ping Test
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => triggerAction(row, 'force-reactivate')}
                              data-testid={`webhook-health-action-reactivate-${row.id}`}
                            >
                              <Power className="h-3.5 w-3.5 mr-2" />
                              Force Reactivate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => triggerAction(row, 'revoke-secret')}
                              className="text-rose-600 focus:text-rose-700"
                              data-testid={`webhook-health-action-revoke-${row.id}`}
                            >
                              <KeyRound className="h-3.5 w-3.5 mr-2" />
                              Revoke Secret
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>

      {/* Secret reveal dialog (one-shot, displayed after revoke) */}
      <Dialog open={!!revealedSecret} onOpenChange={(o) => { if (!o) setRevealedSecret(null); }}>
        <DialogContent data-testid="webhook-health-secret-dialog">
          <DialogHeader>
            <DialogTitle>New signing secret</DialogTitle>
            <DialogDescription>
              This is the only time the full secret will be shown. Copy it now and update {revealedSecret?.endpointName || 'the endpoint'} on the receiving system.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted/40 rounded-lg p-3 font-mono text-xs break-all border" data-testid="webhook-health-secret-value">
            {revealedSecret?.secret}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (revealedSecret?.secret) {
                  navigator.clipboard.writeText(revealedSecret.secret);
                  toast.success('Secret copied to clipboard');
                }
              }}
              data-testid="webhook-health-secret-copy-btn"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy secret
            </Button>
            <Button
              onClick={() => setRevealedSecret(null)}
              data-testid="webhook-health-secret-done-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
